/**
 * contentCalendarService.ts
 * Service layer for Module 5 — Content Planning Engine.
 *
 * All functions wrap the FastAPI endpoints documented in:
 *   api/endpoints/social_media_manager/content_planning_routes.py
 *
 * Error handling follows the project-wide convention:
 *   • Success → returns typed data directly
 *   • Failure → throws structured error { message, response: { data, status } }
 *     or returns CPEServiceError for non-throwing helpers
 */

import api, { API_BASE_URL } from './api';
import type {
  ContentCalendar,
  ContentPost,
  CampaignCluster,
  Observance,
  PostStatus,
  ExportFormat,
  RecalculateResponse,
  GenerateCalendarRequest,
  UpdatePostRequest,
  WeeklyTheme,
} from '../types/contentCalendar';

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Pass through to the central api handler to ensure 
  // consistent 401 handling, session activity touches, etc.
  return api.request<T>(endpoint, options);
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: unwrap standard { status, data } envelope
// ─────────────────────────────────────────────────────────────────────────────

function unwrap<T>(envelope: { status?: string; data?: T } & Partial<T>): T {
  // If the backend returned a `data` key, return that; otherwise return the
  // envelope itself (some endpoints return the object directly).
  return (envelope.data ?? envelope) as T;
}

// ─────────────────────────────────────────────────────────────────────────────
// Mapping functions to adapt backend JSON schemas to frontend types
// ─────────────────────────────────────────────────────────────────────────────

function mapBackendPostToFrontend(post: any): ContentPost {
  if (!post) return post;
  return {
    id: post.postId ?? post.id,
    calendarId: post.calendarId,
    week: post.weeklyThemeLabel ? parseInt(post.weeklyThemeLabel.replace('W', '')) : (post.week ?? 1),
    scheduledDate: post.scheduledDate,
    channel: post.platform ?? post.channel,
    themeType: post.themeType,
    contentFormat: post.contentFormat,
    hook: post.creativeCopy ? post.creativeCopy.split('\n')[0] : (post.hook ?? ''),
    creativeCopy: post.creativeCopy ?? '',
    caption: post.caption ?? '',
    hashtags: post.hashtags ?? [],
    status: post.status,
    userEdited: !!post.userEdited,
    userDirection: post.userDirection ?? null,
    createdAt: post.createdAt ?? '',
    updatedAt: post.updatedAt ?? '',
    campaignName: post.campaignName ?? null,
    imageUrl: post.image_url ?? post.imageUrl ?? null,
    carouselSlideUrls: post.carousel_slide_urls ?? post.carouselSlideUrls ?? [],
    imageGenStatus: post.image_gen_status ?? post.imageGenStatus ?? 'NOT_STARTED',
    imageOptions: post.image_options ?? post.imageOptions ?? null,
    image_options: post.image_options ?? post.imageOptions ?? null,
    selectedImageIndex: post.selected_image_index ?? post.selectedImageIndex ?? null,
    selected_image_index: post.selected_image_index ?? post.selectedImageIndex ?? null,
  };
}

function mapBackendCalendarToFrontend(cal: any): ContentCalendar {
  if (!cal) return cal;

  // Convert posts first so we have them to calculate themes
  const postsArray = Array.isArray(cal.posts)
    ? cal.posts.map(mapBackendPostToFrontend)
    : [];

  // Map from post's themeType to strategic theme clusters
  const THEME_CLUSTER_MAP: Record<string, string> = {
    AuthorityPost:    'innovation',
    EducationalPost:  'innovation',
    ProductPost:      'product_education',
    ServicePost:      'product_education',
    TestimonialPost:  'social_proof',
    AwarenessPost:    'social_proof',
    AwardPost:        'social_proof',
    EngagementPost:   'community',
    EventPost:        'community',
    CampaignPost:     'community',
    OfferPost:        'product_education',
    PromotionalPost:  'product_education',
    ObservancePost:   'community',
  };

  const CLUSTER_TO_WEEK_THEME: Record<string, { themeLabel: string; pillar: string; focus: string }> = {
    innovation:        { themeLabel: 'Innovation & Trends',      pillar: 'Thought Leadership', focus: 'Position brand as forward-thinking' },
    product_education: { themeLabel: 'Product Education',        pillar: 'Education',          focus: 'Deep-dive into key features' },
    social_proof:      { themeLabel: 'Social Proof',             pillar: 'Trust',              focus: 'Showcase customer wins and case studies' },
    community:         { themeLabel: 'Community & Culture',      pillar: 'Retention',          focus: 'Engage audience and build belonging' },
  };

  const weeklyThemesArray: WeeklyTheme[] = [];

  // Group posts by week and calculate the dominant cluster for each week
  for (let w = 1; w <= 4; w++) {
    const postsInWeek = postsArray.filter(p => p.week === w);
    const clusterCounts: Record<string, number> = {};
    
    postsInWeek.forEach(post => {
      const cluster = THEME_CLUSTER_MAP[post.themeType] || 'community';
      clusterCounts[cluster] = (clusterCounts[cluster] || 0) + 1;
    });

    let dominantCluster = '';
    let maxCount = -1;
    Object.entries(clusterCounts).forEach(([cluster, count]) => {
      if (count > maxCount) {
        maxCount = count;
        dominantCluster = cluster;
      }
    });

    // Fallback sequentially if no posts/themes for this week
    if (!dominantCluster) {
      const fallbacks = ['innovation', 'product_education', 'social_proof', 'community'];
      dominantCluster = fallbacks[w - 1];
    }

    const themeInfo = CLUSTER_TO_WEEK_THEME[dominantCluster];
    weeklyThemesArray.push({
      week: w as 1 | 2 | 3 | 4,
      themeLabel: themeInfo.themeLabel,
      pillar: themeInfo.pillar,
      focus: themeInfo.focus,
      postCount: postsInWeek.length,
    });
  }

  weeklyThemesArray.sort((a, b) => a.week - b.week);

  return {
    id: cal.calendarId ?? cal.id,
    userId: cal.userId,
    month: cal.month,
    status: cal.status,
    weeklyThemes: weeklyThemesArray,
    posts: postsArray,
    campaigns: Array.isArray(cal.campaigns) ? cal.campaigns : [],
    totalPosts: cal.totalPostsAllotted ?? cal.totalPosts ?? postsArray.length,
    createdAt: cal.generatedAt ?? cal.createdAt ?? '',
    updatedAt: cal.updatedAt ?? cal.generatedAt ?? '',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. Generate Calendar
//    POST /content-calendar/generate
// ─────────────────────────────────────────────────────────────────────────────

export async function generateCalendar(
  userId: string,
  month: string,
  forceRegenerate = false
): Promise<ContentCalendar> {
  const body: GenerateCalendarRequest = { userId, month, forceRegenerate };
  const envelope = await request<any>('/content-calendar/generate', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return mapBackendCalendarToFrontend(unwrap<any>(envelope));
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. Get Calendar
//    GET /content-calendar/{userId}/{month}
// ─────────────────────────────────────────────────────────────────────────────

export async function getCalendar(
  userId: string,
  month: string
): Promise<ContentCalendar> {
  const envelope = await request<any>(
    `/content-calendar/${encodeURIComponent(userId)}/${encodeURIComponent(month)}`
  );
  return mapBackendCalendarToFrontend(unwrap<any>(envelope));
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. Get Posts by Week
//    GET /content-calendar/{calendarId}/posts?week={week}
// ─────────────────────────────────────────────────────────────────────────────

export async function getPostsByWeek(
  calendarId: string,
  week?: 1 | 2 | 3 | 4
): Promise<ContentPost[]> {
  const qs = week !== undefined ? `?week=${week}` : '';
  const envelope = await request<any>(
    `/content-calendar/${encodeURIComponent(calendarId)}/posts${qs}`
  );
  // Response model is PostListResponse → { posts: ContentPost[] }
  const data = unwrap<{ posts?: any[] } | any[]>(envelope);
  const posts = Array.isArray(data) ? data : (data as any).posts ?? [];
  return posts.map(mapBackendPostToFrontend);
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. Update Post
//    PATCH /content-calendar/posts/{postId}
// ─────────────────────────────────────────────────────────────────────────────

export async function updatePost(
  postId: string,
  fields: UpdatePostRequest
): Promise<ContentPost> {
  const envelope = await request<any>(
    `/content-calendar/posts/${encodeURIComponent(postId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(fields),
    }
  );
  return mapBackendPostToFrontend(unwrap<any>(envelope));
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. Update Post Status
//    PATCH /content-calendar/posts/{postId}/status
// ─────────────────────────────────────────────────────────────────────────────

export async function updatePostStatus(
  postId: string,
  status: PostStatus
): Promise<ContentPost> {
  const envelope = await request<any>(
    `/content-calendar/posts/${encodeURIComponent(postId)}/status`,
    {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }
  );
  return mapBackendPostToFrontend(unwrap<any>(envelope));
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. Get Campaign Clusters
//    GET /content-calendar/{calendarId}/campaigns
// ─────────────────────────────────────────────────────────────────────────────

export async function getCampaigns(calendarId: string): Promise<CampaignCluster[]> {
  const envelope = await request<any>(
    `/content-calendar/${encodeURIComponent(calendarId)}/campaigns`
  );
  const data = unwrap<{ campaigns?: CampaignCluster[] } | CampaignCluster[]>(envelope);
  return Array.isArray(data) ? data : (data as any).campaigns ?? [];
}

// ─────────────────────────────────────────────────────────────────────────────
// 7. Get Observances
//    GET /observances?month={month}
// ─────────────────────────────────────────────────────────────────────────────

export async function getObservances(month: string): Promise<Observance[]> {
  const envelope = await request<any>(
    `/observances?month=${encodeURIComponent(month)}`
  );
  const data = unwrap<{ observances?: Observance[] } | Observance[]>(envelope);
  return Array.isArray(data) ? data : (data as any).observances ?? [];
}

// ─────────────────────────────────────────────────────────────────────────────
// 8. Save Observances
//    POST /social-setup/observances
// ─────────────────────────────────────────────────────────────────────────────

export async function saveObservances(
  userId: string,
  selectedObservances: string[]
): Promise<void> {
  await request<any>('/social-setup/observances', {
    method: 'POST',
    body: JSON.stringify({ userId, selectedObservances }),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// 9. Export Calendar
//    GET /content-calendar/{calendarId}/export?format=CSV|PDF
//    Returns raw binary Blob for download.
// ─────────────────────────────────────────────────────────────────────────────

export async function exportCalendar(
  calendarId: string,
  format: ExportFormat = 'CSV'
): Promise<Blob> {
  const token = localStorage.getItem('access_token');
  const url = `${API_BASE_URL}/content-calendar/${encodeURIComponent(calendarId)}/export?format=${format}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error(`Export failed: ${response.status} ${response.statusText}`);
  }

  return response.blob();
}

/**
 * Convenience: trigger a browser download of the exported calendar.
 */
export async function downloadCalendar(
  calendarId: string,
  format: ExportFormat = 'CSV',
  filename?: string
): Promise<void> {
  const blob = await exportCalendar(calendarId, format);
  const extension = format === 'CSV' ? 'csv' : 'pdf';
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `content_calendar.${extension}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────────────────────────────────────
// 10. Recalculate Calendar
//     POST /content-calendar/{calendarId}/recalculate
// ─────────────────────────────────────────────────────────────────────────────

export async function recalculateCalendar(
  calendarId: string
): Promise<RecalculateResponse> {
  const envelope = await request<any>(
    `/content-calendar/${encodeURIComponent(calendarId)}/recalculate`,
    { method: 'POST' }
  );
  return unwrap<RecalculateResponse>(envelope);
}

// ─────────────────────────────────────────────────────────────────────────────
// Composite helper: fetch calendar or generate if not found (CPE-002 guard)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Try to load an existing calendar for the user+month.
 * If none exists (404 / CPE-002), returns null without throwing.
 * Components use this on mount to decide whether to show the empty state
 * or the loaded calendar.
 */
export async function fetchOrNull(
  userId: string,
  month: string
): Promise<ContentCalendar | null> {
  try {
    return await getCalendar(userId, month);
  } catch (err: any) {
    const status = err?.response?.status;
    const code = err?.response?.data?.detail?.errorCode ??
                 err?.response?.data?.errorCode ??
                 err?.response?.data?.code ??
                 err?.response?.data?.error_code;
    
    // 404 = no calendar (expected)
    // 409 = backend returned conflict/exists code CPE-002/CPE-008 semantics
    if (status === 404 || status === 409 || code === 'CPE-002' || code === 'CPE-008') {
      return null;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Singleton default export (mirrors strategyService.ts pattern)
// ─────────────────────────────────────────────────────────────────────────────

const contentCalendarService = {
  generateCalendar,
  getCalendar,
  getPostsByWeek,
  updatePost,
  updatePostStatus,
  getCampaigns,
  getObservances,
  saveObservances,
  exportCalendar,
  downloadCalendar,
  recalculateCalendar,
  fetchOrNull,
};

export default contentCalendarService;